import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, MapPin, Trash2 } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { humanizeFieldName } from './docIntelUtils';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const MIN_REGION_SIZE = 0.01;

const DocIntelPdfRegionTagger = ({ url, fields, onRegionChange }) => {
  const pageRef = useRef(null);
  const [pageCount, setPageCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageWidth, setPageWidth] = useState(500);
  const [selectedFieldName, setSelectedFieldName] = useState(
    fields[0]?.canonical_name || ''
  );
  const [drag, setDrag] = useState(null);

  useEffect(() => {
    if (!fields.some((field) => field.canonical_name === selectedFieldName)) {
      setSelectedFieldName(fields[0]?.canonical_name || '');
    }
  }, [fields, selectedFieldName]);

  useEffect(() => {
    const element = pageRef.current;
    if (!element) return undefined;
    const updateWidth = () => setPageWidth(Math.max(280, Math.min(680, element.clientWidth - 2)));
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const pageRegions = useMemo(
    () =>
      fields.filter(
        (field) => field.region?.page === pageNumber && field.region.width && field.region.height
      ),
    [fields, pageNumber]
  );

  const getPoint = (event) => {
    const rect = pageRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height))
    };
  };

  const startDrag = (event) => {
    if (!selectedFieldName) return;
    const point = getPoint(event);
    if (!point) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setDrag({ start: point, end: point });
  };

  const moveDrag = (event) => {
    if (!drag) return;
    const point = getPoint(event);
    if (point) setDrag((current) => ({ ...current, end: point }));
  };

  const finishDrag = () => {
    if (!drag || !selectedFieldName) return;
    const x = Math.min(drag.start.x, drag.end.x);
    const y = Math.min(drag.start.y, drag.end.y);
    const width = Math.abs(drag.end.x - drag.start.x);
    const height = Math.abs(drag.end.y - drag.start.y);
    setDrag(null);
    if (width < MIN_REGION_SIZE || height < MIN_REGION_SIZE) return;
    onRegionChange(selectedFieldName, { page: pageNumber, x, y, width, height });
  };

  const dragStyle = drag
    ? {
        left: `${Math.min(drag.start.x, drag.end.x) * 100}%`,
        top: `${Math.min(drag.start.y, drag.end.y) * 100}%`,
        width: `${Math.abs(drag.end.x - drag.start.x) * 100}%`,
        height: `${Math.abs(drag.end.y - drag.start.y) * 100}%`
      }
    : null;

  return (
    <div className="flex min-h-0 flex-col">
      <div className="border-b border-slate-200 bg-white p-3">
        <div className="flex flex-wrap items-center gap-2">
          <MapPin className="h-4 w-4 text-teal-700" />
          <select
            value={selectedFieldName}
            onChange={(event) => setSelectedFieldName(event.target.value)}
            className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs">
            <option value="">Select a field to tag</option>
            {fields.map((field) => (
              <option key={field.canonical_name} value={field.canonical_name}>
                {humanizeFieldName(field.canonical_name)}
                {field.region ? ` · page ${field.region.page}` : ''}
              </option>
            ))}
          </select>
          {selectedFieldName &&
            fields.find((field) => field.canonical_name === selectedFieldName)?.region && (
              <button
                type="button"
                onClick={() => onRegionChange(selectedFieldName, null)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
                title="Clear field region">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
        </div>
        <p className="mt-2 text-[11px] text-slate-500">
          Select a field, then drag a box over its value in the PDF.
        </p>
      </div>

      <div className="min-h-[340px] flex-1 overflow-auto bg-slate-100 p-3">
        <Document
          file={url}
          loading={<p className="py-12 text-center text-sm text-slate-500">Loading PDF…</p>}
          onLoadSuccess={({ numPages }) => {
            setPageCount(numPages);
            setPageNumber((current) => Math.min(current, numPages));
          }}>
          <div
            ref={pageRef}
            className={`relative mx-auto w-fit select-none shadow-sm ${
              selectedFieldName ? 'cursor-crosshair' : ''
            }`}
            onPointerDown={startDrag}
            onPointerMove={moveDrag}
            onPointerUp={finishDrag}
            onPointerCancel={() => setDrag(null)}>
            <Page pageNumber={pageNumber} width={pageWidth} />
            <div className="pointer-events-none absolute inset-0">
              {pageRegions.map((field) => (
                <div
                  key={field.canonical_name}
                  className={`absolute border-2 ${
                    field.canonical_name === selectedFieldName
                      ? 'border-teal-600 bg-teal-300/25'
                      : 'border-sky-500 bg-sky-300/20'
                  }`}
                  style={{
                    left: `${field.region.x * 100}%`,
                    top: `${field.region.y * 100}%`,
                    width: `${field.region.width * 100}%`,
                    height: `${field.region.height * 100}%`
                  }}
                  title={humanizeFieldName(field.canonical_name)}
                />
              ))}
              {dragStyle && (
                <div
                  className="absolute border-2 border-dashed border-teal-700 bg-teal-300/25"
                  style={dragStyle}
                />
              )}
            </div>
          </div>
        </Document>
      </div>

      {pageCount > 0 && (
        <div className="flex items-center justify-center gap-3 border-t border-slate-200 bg-white px-3 py-2">
          <button
            type="button"
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber((page) => page - 1)}
            className="rounded p-1 text-slate-600 hover:bg-slate-100 disabled:opacity-30">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs text-slate-600">
            Page {pageNumber} of {pageCount}
          </span>
          <button
            type="button"
            disabled={pageNumber >= pageCount}
            onClick={() => setPageNumber((page) => page + 1)}
            className="rounded p-1 text-slate-600 hover:bg-slate-100 disabled:opacity-30">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default DocIntelPdfRegionTagger;
